import axiosAPI from "axios";
import * as qs from "qs";

import { API_URL, API_KEY, getAuthToken, getDeviceHeaders } from "./common-utils";
import {
  CUSTOM_ERROR_CODES,
  handleDBConflict,
  handleUnauthorized,
} from "./api-error-utils";
import { refreshAuthToken } from "../lib/api/client";
import ENDPOINTS from "../lib/api/endpoints";

const getOptions = (headers, withoutToken, data) => {
  const authToken = getAuthToken();

  return {
    headers: {
      ...(authToken &&
        !withoutToken && { Authorization: `Bearer ${authToken}` }),
      "x-api-key": API_KEY(),
      ...headers,
      ...getDeviceHeaders(),
    },
    ...(data && {
      params: data,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat" }),
    }),
  };
};

const prepareUrl = (api) => API_URL() + api;

/**
 * 401 -> refresh once -> replay once.
 *
 * `__isRetry` and the refresh-route check are the loop guards. Without them a
 * 401 on `/login/refresh/` re-entered this handler, which fired the refresh
 * again, forever — and each pass also triggered a redirect, so the app both
 * flooded the backend and reloaded itself without end.
 *
 * The refresh itself comes from `lib/api/client`, which is single-flight: a
 * screen firing six requests into a dead session mints one new token, not six.
 */
const handleAPIError = async (error) => {
  const { response, config } = error;

  const responseStatusCode = response && response.status;

  if (!responseStatusCode || !CUSTOM_ERROR_CODES.includes(responseStatusCode)) {
    return Promise.reject(error);
  }

  if (responseStatusCode === 409) return handleDBConflict();

  const isRefreshCall =
    config && String(config.url || "").includes(ENDPOINTS.AUTH.refresh);

  // A 401 on the refresh route is terminal — there is no second token to try.
  if (!config || config.__isRetry || isRefreshCall) {
    return handleUnauthorized(error);
  }

  config.__isRetry = true;

  const authToken = await refreshAuthToken();

  if (!authToken) return handleUnauthorized(error);

  // `config.headers` is an AxiosHeaders instance here, not a plain object, so
  // go through its API — assigning the key directly can leave the stale
  // bearer sitting next to the new one under a different casing.
  if (config.headers && typeof config.headers.set === "function") {
    config.headers.set("Authorization", `Bearer ${authToken}`);
  } else {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${authToken}`,
    };
  }

  return axiosAPI.request(config).catch(handleAPIError);
};

const axios = {
  get: (api, headers, data = {}) =>
    axiosAPI
      .get(prepareUrl(api), getOptions(headers, false, data))
      .catch(handleAPIError),
  put: (api, data = {}, headers, withoutToken = false) =>
    axiosAPI
      .put(prepareUrl(api), data, getOptions(headers, withoutToken))
      .catch(handleAPIError),
  updateFile: (api, data = {}, headers, isFile = false) =>
    axiosAPI.put(api, data, getOptions(headers, isFile)),
  post: (api, formData, headers, withoutToken = false) =>
    axiosAPI
      .post(prepareUrl(api), formData, getOptions(headers, withoutToken))
      .catch(handleAPIError),
  patch: (api, formData, headers) =>
    axiosAPI
      .patch(prepareUrl(api), formData, getOptions(headers))
      .catch(handleAPIError),
  delete: (api, headers) =>
    axiosAPI.delete(prepareUrl(api), getOptions(headers)).catch(handleAPIError),
};

export default axios;
